import { Button, type ButtonProps } from "@/components/ui/button"
import { Link } from "@/i18n/navigation"

interface LinkButtonProps extends Omit<ButtonProps, "asChild"> {
  href?: string
  external?: boolean
}

// A Button that renders a link when `href` is set, a plain button otherwise.
// Button only paints iconLeft/iconRight in its native branch, so compose them
// into the link children here to keep icons in both shapes.
// Slot can't swap a link's children for the loading Dots, so an inactive
// LinkButton renders the native button branch: real disabled semantics,
// aria-busy, and the Dots — an unusable link shouldn't be a link anyway.
export const LinkButton = ({ href, external, iconLeft, iconRight, children, ...rest }: LinkButtonProps) => {
  if (!href || rest.disabled || rest.loading) {
    return (
      <Button iconLeft={iconLeft} iconRight={iconRight} {...rest}>
        {children}
      </Button>
    )
  }

  const inner = (
    <>
      {iconLeft}
      {children}
      {iconRight}
    </>
  )

  return (
    <Button asChild {...rest}>
      {external ? (
        <a href={href} target="_blank" rel="noreferrer">
          {inner}
        </a>
      ) : (
        <Link href={href}>{inner}</Link>
      )}
    </Button>
  )
}
